import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from '@angular/core';
import FileSaver from 'file-saver';
import { List } from 'immutable';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';

import { BaseComponent } from '../../../../components/base/base.component';
import { INodeInstanceViewModel } from '../../../../models/node/node-instance.view.model';
import { IProjectModel } from '../../../../models/project/project.model';
import { IWorkspaceInstanceModel } from '../../../../models/workspace/workspace-instance.model';
import {
  AUDIT_HISTORY_ACTION_LABELS,
  AUDIT_HISTORY_INTERNAL_SOURCE_APPS,
  AUDIT_HISTORY_SOURCE_APP_LABELS,
  AuditHistoryAction,
  AuditHistoryActions,
  AuditHistoryScope,
  AuditHistoryScopes,
  IAuditHistoryChange,
  IAuditHistoryEntry,
  IAuditHistoryFilterOption,
  IAuditHistoryQuery,
} from '../../../../models/history/audit-history.model';
import { AuditHistoryStore } from '../../../../services/stores/audit-history.store';
import { DashboardHelper } from '../../../../services/dashboard.service';
import { exportFileDateFormat } from '../../../../services/helpers/date.helper';
import { convertAuditHistoryExportToLocalTime } from '../../../../services/helpers/audit-history-export.helper';

/**
 * One line of the history table.
 *
 * Entries are flattened to one row per change, so a save that altered three fields reads as three
 * rows rather than one cell holding three. That keeps every column meaningful on every row - which
 * is what makes the table scannable - and matches the CSV the export produces, so what a user sees
 * and what they download are the same shape.
 *
 * An entry that carries no changes (a creation, a deletion) still produces a row: "this happened,
 * with no field detail" is itself part of the trail.
 */
export interface IHistoryRow {
  key: string;
  entry: IAuditHistoryEntry;
  /** True on the first row of a multi-row entry, so the table can group them visually. */
  startsEntry: boolean;
  /**
   * The timestamp to render, or null when the stored value will not parse.
   *
   * Validated here rather than in the template because the date pipe throws on an unparseable
   * value, and a throw during change detection takes the whole tab down - no rows, no error state,
   * no Retry. One malformed row must cost only that row's timestamp.
   */
  when: string | null;
  field: string;
  oldValue: string;
  newValue: string;
  truncated: boolean;
  /** No field detail on this row - render the action across the value columns instead. */
  actionOnly: boolean;
}

/** Actions that concern one step, and so need its number alongside the field name. */
const STEP_ACTIONS: AuditHistoryAction[] = [
  AuditHistoryActions.StepAdded,
  AuditHistoryActions.StepUpdated,
  AuditHistoryActions.StepDeleted,
  AuditHistoryActions.StepAttachmentAdded,
  AuditHistoryActions.StepAttachmentRemoved,
];

/**
 * A full ISO-8601 instant. Date fields store one, and it has to be rendered as a date rather than
 * shown raw - see formatIfTimestamp. Requires the date, the T and a time, so that a value which
 * merely begins with digits is left exactly as it was recorded.
 */
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

/**
 * The edit panel's History tab.
 *
 * On-demand by requirement, and that is the one thing about this component that must not drift:
 *
 *  - it fetches only when `isActive` becomes true, never on node selection;
 *  - selecting a different node while the tab is closed loads nothing, it only drops what is held
 *    so the next activation refetches rather than flashing the previous item's history;
 *  - the tab carries **no count badge**, unlike every other tab in the panel. A count would need a
 *    query on every node selection, which is exactly the cost this feature was asked not to add.
 *    The asymmetry is deliberate - please do not "fix" it.
 */
@Component({
  selector: 'app-edit-panel-history-pane',
  templateUrl: './edit-panel-history-pane.component.html',
  styleUrls: ['./edit-panel-history-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditPanelHistoryPaneComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedNode: INodeInstanceViewModel | null = null;
  @Input() selectedProject: IProjectModel | null = null;
  /**
   * The label (board) the edit panel is currently open under - the source of LABELKEY in the
   * export filename. Not the tab's own label filter dropdown: that offers only an id and a display
   * name (see IAuditHistoryFilterOption), never the short backend-generated key ("BUGT-7") that
   * ITreeNode/IWorkspaceInstanceModel carry, so it cannot supply this on its own.
   */
  @Input() selectedWorkspace: IWorkspaceInstanceModel | null = null;
  /** True while the History tab is the panel's active tab. Nothing is fetched until it is. */
  @Input() isActive = false;
  /**
   * Whether the selected item is a Test, taken from the container's blueprint-derived `isTestNode`.
   *
   * Only a Test has a step table or per-label runs, so only a Test can ever have Steps or Runs
   * history. Offering those chips on an Epic is dead UI that always reports nothing.
   *
   * Deliberately not `canShowStepsTab`/`canShowRunsTab`: those also encode whether the tab is
   * visible *right now* (they go false for a selected test step in the Do view), which says nothing
   * about whether the item's recorded history can contain step or run entries.
   */
  @Input() isTestNode = false;

  public entries: List<IAuditHistoryEntry> = List<IAuditHistoryEntry>([]);
  /** The flattened table view of `entries` - see IHistoryRow. */
  public rows: IHistoryRow[] = [];
  public isLoading = false;
  public lastError: string | null = null;
  /** The page currently held, and how many the current filter has in total - see the store. */
  public currentPage = 1;
  public totalCount = 0;
  /** How many entries a page holds. Mirrors the store's default until ngOnInit subscribes. */
  public pageSize = 10;
  public readonly pageSizeOptions = AuditHistoryStore.PageSizeOptions;
  public isExporting = false;
  /**
   * Kept apart from lastError on purpose. A failed export has not touched the list that is on
   * screen, so reporting it as "Couldn't load history" next to a Retry that reloads would describe
   * the wrong failure and offer the wrong remedy.
   */
  public exportError: string | null = null;

  /**
   * How long typing pauses before the search fires. Matches the admin log screen so the two feel
   * the same; long enough that a word is one request, short enough not to feel unresponsive.
   */
  private static readonly SearchDebounceMs = 400;

  /** Scopes only a Test item can ever produce history in. */
  private static readonly TestOnlyScopes: AuditHistoryScope[] = [AuditHistoryScopes.Steps, AuditHistoryScopes.Run];

  private static readonly AllScopeFilters: { id: AuditHistoryScope | null; label: string }[] = [
    { id: null, label: 'All' },
    { id: AuditHistoryScopes.Node, label: 'Fields' },
    { id: AuditHistoryScopes.Steps, label: 'Steps' },
    { id: AuditHistoryScopes.Attachments, label: 'Attachments' },
    { id: AuditHistoryScopes.Run, label: 'Runs' },
  ];

  /** Recomputed when the item changes rather than evaluated per change-detection pass. */
  public scopeFilters: { id: AuditHistoryScope | null; label: string }[] = [];

  public activeScope: AuditHistoryScope | null = null;

  /**
   * Every event type's display label and declared order, keyed by the raw action - not what the
   * filter offers by itself. `actionTypeFilters` below narrows this to the event types this item's
   * history actually contains, fetched from the server the same way the Actor and Label dropdowns
   * are: a fixed list of every possible action would be mostly dead options on any one item (an
   * Epic will never have a StepAdded row, and even a Test's history may never have used every
   * action), and a filter with an incomplete-looking list is worse than a short, honest one.
   */
  private static readonly AllActionTypeFilters: IAuditHistoryFilterOption[] = (Object.keys(AuditHistoryActions) as (keyof typeof AuditHistoryActions)[]).map((key) => {
    const action = AuditHistoryActions[key];
    return { id: action, label: AUDIT_HISTORY_ACTION_LABELS[action] };
  });

  /** Populated from the item's whole history via $filterOptions, same as actorOptions/labelOptions. */
  public actionTypeFilters: IAuditHistoryFilterOption[] = [];

  // --- filter bar -------------------------------------------------------------------------------
  // Everything here narrows the *server* query, not the rows already loaded. Filtering client-side
  // would only filter the page that has been scrolled into view, which is the same trap the CSV
  // export avoids by being built server-side.

  public searchText = '';
  public selectedActorIds: string[] = [];
  public selectedLabelIds: string[] = [];
  public selectedActionTypes: AuditHistoryAction[] = [];
  public fromDate: Date | null = null;
  public toDate: Date | null = null;

  /** Populated from the item's whole history, not from the rows on screen - see the store. */
  public actorOptions: IAuditHistoryFilterOption[] = [];
  public labelOptions: IAuditHistoryFilterOption[] = [];
  /** True when the last attempt to load the dropdown options above failed - see the store. */
  public filterOptionsFailed = false;

  /**
   * A dropdown holding exactly one option can't narrow anything - every row in the item's whole
   * history already matches the only value there is to pick. Disabled with a tooltip saying why,
   * rather than left clickable and pointless (worse: it invites a click that "selects" the one
   * thing already implied).
   *
   * Not applied to the empty/"nothing loaded yet" state (0 options) - that is a normal, pickable
   * "All ..." placeholder, not a single value standing in everyone's way.
   */
  public get actorFilterDisabled(): boolean {
    return this.actorOptions.length === 1;
  }

  public get actorFilterTooltip(): string | null {
    if (!this.actorFilterDisabled) {
      return null;
    }
    return `Only ${this.actorOptions[0]?.label || 'one person'} has changed this item`;
  }

  public get labelFilterDisabled(): boolean {
    return this.labelOptions.length === 1;
  }

  public get labelFilterTooltip(): string | null {
    if (!this.labelFilterDisabled) {
      return null;
    }
    return `Every change was recorded under ${this.labelOptions[0]?.label || 'one label'}`;
  }

  public get actionTypeFilterDisabled(): boolean {
    return this.actionTypeFilters.length === 1;
  }

  public get actionTypeFilterTooltip(): string | null {
    if (!this.actionTypeFilterDisabled) {
      return null;
    }
    return `Every recorded change is "${this.actionTypeFilters[0]?.label || 'the same type'}"`;
  }

  /**
   * Free text is debounced; dropdowns and dates refresh immediately. Matches the admin log screen,
   * and for the same reason: a request per keystroke is both wasteful and visibly jumpy.
   */
  private readonly filterInput$ = new Subject<void>();
  private filterSub?: Subscription;

  /** Whether anything other than the scope chips is narrowing the list - drives the Reset control. */
  public get hasActiveFilters(): boolean {
    return !!(
      this.searchText?.trim() ||
      this.selectedActorIds.length ||
      this.selectedLabelIds.length ||
      this.selectedActionTypes.length ||
      this.fromDate ||
      this.toDate ||
      this.activeScope
    );
  }

  private entriesSub?: Subscription;
  private loadingSub?: Subscription;
  private errorSub?: Subscription;
  private pageNumberSub?: Subscription;
  private totalCountSub?: Subscription;
  private pageSizeSub?: Subscription;
  private modifiedSub?: Subscription;
  private exportSub?: Subscription;
  private filterOptionsSub?: Subscription;
  private filterOptionsFailedSub?: Subscription;

  constructor(
    private readonly historyStore: AuditHistoryStore,
    private readonly dashboardHelper: DashboardHelper,
    private readonly cdr: ChangeDetectorRef,
    // Last, optional and defaulted, because this component is also constructed directly in its
    // spec rather than through TestBed - a required parameter here would break every one of those.
    @Optional() @Inject(LOCALE_ID) private readonly locale: string = 'en-US',
  ) {
    super();
  }

  public ngOnInit(): void {
    this.rebuildScopeFilters();

    this.entriesSub = this.historyStore.$entries.subscribe((entries) => {
      this.entries = entries ?? List<IAuditHistoryEntry>([]);
      this.rows = this.buildRows(this.entries);
      this.cdr.markForCheck();
    });
    this.loadingSub = this.historyStore.$isLoading.subscribe((v) => {
      this.isLoading = v;
      this.cdr.markForCheck();
    });
    this.errorSub = this.historyStore.$lastError.subscribe((v) => {
      this.lastError = v;
      this.cdr.markForCheck();
    });
    // Guarded against 0: the store reports that only while nothing has been loaded (or after
    // clear()), and the pager is never on screen then anyway - but "Page 0 of 1" would be a poor
    // value to have briefly held if a subscriber ever read it during that window.
    this.pageNumberSub = this.historyStore.$pageNumber.subscribe((v) => {
      this.currentPage = Math.max(1, v);
      this.cdr.markForCheck();
    });
    this.totalCountSub = this.historyStore.$totalCount.subscribe((v) => {
      this.totalCount = v;
      this.cdr.markForCheck();
    });
    this.pageSizeSub = this.historyStore.$pageSize.subscribe((v) => {
      this.pageSize = v;
      this.cdr.markForCheck();
    });
    this.filterOptionsSub = this.historyStore.$filterOptions.subscribe((options) => {
      this.actorOptions = this.withSelectableLabel(options?.actors);
      this.labelOptions = this.withSelectableLabel(options?.labels);
      this.actionTypeFilters = this.buildActionTypeFilters(options?.actions);
      this.cdr.markForCheck();
    });
    this.filterOptionsFailedSub = this.historyStore.$filterOptionsFailed.subscribe((failed) => {
      this.filterOptionsFailed = failed;
      this.cdr.markForCheck();
    });

    this.filterSub = this.filterInput$.pipe(debounceTime(EditPanelHistoryPaneComponent.SearchDebounceMs)).subscribe(() => this.reload());

    // Refresh after the item is saved while this tab is open. Consistent with the on-demand rule
    // rather than an exception to it: this fires on the user's own explicit save, not passively on
    // selection, and without it a change made with History open only appears after reopening the tab.
    //
    // skip(1) drops the value the BehaviorSubject replays on subscribe - that is the *previous*
    // save, already reflected by the activation load below, so acting on it would double-fetch.
    //
    // Refetches rather than splicing in a synthesised row: the recorded entry carries server-resolved
    // status text, option ids, the actor snapshot and the authoritative timestamp, none of which the
    // client can reproduce faithfully.
    this.modifiedSub = this.dashboardHelper.modifiedPropertyItem.pipe(skip(1)).subscribe((modified) => {
      if (!this.isActive || !modified?.id || modified.id !== this.nodeId) {
        return;
      }
      this.reload();
    });

    // Covers the case where the panel opens with History already the active tab.
    if (this.isActive) {
      this.reloadForItem();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const nodeChanged = !!changes['selectedNode'] && this.nodeIdOf(changes['selectedNode'].previousValue) !== this.nodeId;

    if (nodeChanged) {
      // The scope chip belongs to the item being looked at, not to the panel.
      this.activeScope = null;
      // A failed export belongs to the item it failed on. Carried over, the banner sits above the
      // new item's history and its Try again exports that item instead - an error attributed to
      // content it never touched.
      this.exportError = null;
      // Same reasoning extends to every filter control: an actor/label id carried over from the
      // previous item may not apply to this one at all, silently narrowing or emptying its history
      // with no visible sign why - the dropdown reads "All users" while the query still holds an id.
      this.searchText = '';
      this.selectedActorIds = [];
      this.selectedLabelIds = [];
      this.selectedActionTypes = [];
      this.fromDate = null;
      this.toDate = null;
    }

    if (nodeChanged || changes['isTestNode']) {
      this.rebuildScopeFilters();
    }

    const becameActive = !!changes['isActive'] && this.isActive && !changes['isActive'].previousValue;

    if (this.isActive && (becameActive || nodeChanged)) {
      this.reloadForItem();
      return;
    }

    if (nodeChanged && !this.isActive) {
      // Closed tab: drop what is held so a later activation cannot flash the previous item's
      // history, but issue no request.
      this.historyStore.clear();
    }
  }

  public ngOnDestroy(): void {
    this.entriesSub?.unsubscribe();
    this.loadingSub?.unsubscribe();
    this.errorSub?.unsubscribe();
    this.pageNumberSub?.unsubscribe();
    this.totalCountSub?.unsubscribe();
    this.pageSizeSub?.unsubscribe();
    this.modifiedSub?.unsubscribe();
    this.exportSub?.unsubscribe();
    this.filterOptionsSub?.unsubscribe();
    this.filterOptionsFailedSub?.unsubscribe();
    this.filterSub?.unsubscribe();
    this.historyStore.clear();
    super.ngOnDestroy?.();
  }

  /**
   * Drops the Steps and Runs chips for anything that is not a Test, since only a Test has a step
   * table or per-label runs to record history against.
   */
  private rebuildScopeFilters(): void {
    this.scopeFilters = EditPanelHistoryPaneComponent.AllScopeFilters.filter((filter) => this.isTestNode || !filter.id || !EditPanelHistoryPaneComponent.TestOnlyScopes.includes(filter.id));

    // Guards the case where the item changes from a Test to something else while one of those chips
    // is selected - the filter would otherwise stay applied with no way to clear it.
    if (this.activeScope && !this.scopeFilters.some((filter) => filter.id === this.activeScope)) {
      this.activeScope = null;
    }
  }

  /**
   * Narrows the fixed label/order list down to the event types this item's history actually
   * contains. Labelled from the static map so the option text always matches the transaction
   * badge; membership comes from the server, the same as actorOptions/labelOptions above.
   */
  private buildActionTypeFilters(present?: AuditHistoryAction[] | null): IAuditHistoryFilterOption[] {
    const known = new Set(present ?? []);
    return EditPanelHistoryPaneComponent.AllActionTypeFilters.filter((filter) => known.has(filter.id as AuditHistoryAction));
  }

  public onScopeSelected(scope: AuditHistoryScope | null): void {
    if (this.activeScope === scope) {
      return;
    }
    this.activeScope = scope;
    // The export that failed was of a different filter, and Try again would now download this one.
    this.exportError = null;
    this.reload();
  }

  /** A dropdown or date changed - refresh now, there is nothing to wait for. */
  public onFilterChange(): void {
    this.exportError = null;
    this.reload();
  }

  /** A keystroke in the search box - debounced, so one word is one request rather than four. */
  public onFilterInput(): void {
    this.exportError = null;
    this.filterInput$.next();
  }

  /**
   * Back to the unfiltered list, including the scope chip.
   *
   * Clears everything at once. The date pickers have their own clear icon and the multi-selects
   * can be unticked value by value; the search box is emptied by editing it.
   */
  public onResetFilters(): void {
    if (!this.hasActiveFilters) {
      return;
    }

    this.searchText = '';
    this.selectedActorIds = [];
    this.selectedLabelIds = [];
    this.selectedActionTypes = [];
    this.fromDate = null;
    this.toDate = null;
    this.activeScope = null;

    this.onFilterChange();
  }

  /** Ceil rather than floor - a filter matching one more than a full page still needs a page for it. */
  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  public get hasPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  public get hasNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  /** "{first}-{last} of {totalRecords}" - the same current-page-report format the admin screens'
   *  own paginated lists use (see user-list.component.html, catchHubLog.component.html). */
  public get rangeLabel(): string {
    if (this.totalCount === 0) {
      return '0 of 0';
    }
    const first = (this.currentPage - 1) * this.pageSize + 1;
    const last = Math.min(this.currentPage * this.pageSize, this.totalCount);
    return `${first}-${last} of ${this.totalCount}`;
  }

  public onFirstPage(): void {
    if (!this.hasPreviousPage || this.isLoading) {
      return;
    }
    this.historyStore.goToPage(1);
  }

  public onPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoading) {
      return;
    }
    this.historyStore.goToPage(this.currentPage - 1);
  }

  public onNextPage(): void {
    if (!this.hasNextPage || this.isLoading) {
      return;
    }
    this.historyStore.goToPage(this.currentPage + 1);
  }

  public onLastPage(): void {
    if (!this.hasNextPage || this.isLoading) {
      return;
    }
    this.historyStore.goToPage(this.totalPages);
  }

  /**
   * Bound to the rows-per-page control via ngModel, so `this.pageSize` is already the new value.
   *
   * Unlike a page navigation, this is never guarded on isLoading: the control has already shown
   * the new size, so refusing the request here would leave that value on screen with no page
   * fetched under it. A page still loading under the old size is superseded cleanly by the store's
   * own request token rather than needing a guard here.
   */
  public onPageSizeChange(): void {
    this.historyStore.setPageSize(this.pageSize);
  }

  public onRetry(): void {
    this.reload();
  }

  /** Retries only the dropdown options - the list itself may already be loaded and correct. */
  public onRetryFilterOptions(): void {
    const nodeId = this.nodeId;
    const projectId = this.selectedProject?.id;
    if (nodeId && projectId) {
      this.historyStore.loadFilterOptions(nodeId, projectId);
    }
  }

  /**
   * Downloads the whole history for this item under the filter on screen.
   *
   * The file comes from the server rather than from `rows`, which holds only the pages loaded so
   * far. Exporting what happens to be on screen would produce a file that looks complete and is not
   * - for an audit trail that is worse than offering no export.
   */
  public onExport(): void {
    if (this.isExporting) {
      return;
    }

    const request = this.historyStore.exportCurrent(this.isTestNode, this.selectedWorkspace?.id ?? null);
    if (!request) {
      return;
    }

    this.isExporting = true;
    this.exportError = null;
    this.exportSub?.unsubscribe();

    this.exportSub = request.subscribe({
      next: (response) => {
        try {
          if (response.body && response.body.length > 0) {
            // The server has no way to know the reader's timezone, so it writes the When column as
            // UTC (see AuditHistoryExportHelper) - the browser is what converts it to local time,
            // the same reason the on-screen table's own timestamps are formatted here rather than
            // server-side (see formatIfTimestamp).
            const csv = convertAuditHistoryExportToLocalTime(response.body);
            const bom = String.fromCharCode(0xfeff);
            const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
            FileSaver.saveAs(blob, this.exportFilename());
          } else {
            this.exportError = 'The export returned no data.';
          }
        } catch {
          // saveAs throws synchronously when the browser blocks the download, outside the
          // observable's error path.
          this.exportError = 'Could not save the export file. Check whether downloads are blocked.';
        } finally {
          this.isExporting = false;
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.exportError = 'Could not export the history.';
        this.isExporting = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * `Audit_history_of_[PROJKEY]_[LABELKEY]_[ITEMKEY]_dd_MM_yyyy_HH_mm_ss.csv` - built here rather
   * than read off the response's Content-Disposition, the same way every other export in this app
   * (do-table, test-step-table, timeline, ...) names its file from on-screen context rather than a
   * server header. Any key that is missing - a label created before the key backfill migration ran,
   * for instance - is left out rather than rendered as "undefined".
   */
  private exportFilename(): string {
    const keys = [this.selectedProject?.key, this.selectedWorkspace?.key, this.selectedNode?.key]
      .map((key) => key?.trim())
      .filter((key): key is string => !!key);

    return ['Audit_history_of', ...keys, exportFileDateFormat(new Date())].join('_') + '.csv';
  }

  public get isEmpty(): boolean {
    return !this.isLoading && !this.lastError && this.entries.size === 0;
  }

  /**
   * Whether the Label column earns its place on the loaded page.
   *
   * The item is opened under one label (`selectedWorkspace`), and most of its history was made
   * there too - repeating that same name on every row says nothing a reader doesn't already know
   * from having the panel open under it. The column is worth showing only once a row's label
   * differs from the one the panel is open under, including a row recorded under no label at all -
   * that is still "not this label".
   *
   * Judged against the item's whole history (`labelOptions`, fetched from the server the same way
   * as the Label dropdown), not just the loaded page: judged per page, a first page made entirely
   * under the current label hid the column, and it popped into existence on page 2 - the table's
   * shape changed as the user paged. The loaded page is still checked as well, so the column is
   * shown even if the filter options failed to load or have not arrived yet.
   */
  public get showLabelColumn(): boolean {
    const currentLabelId = this.selectedWorkspace?.id ?? null;
    return (
      this.labelOptions.some((option) => !!option && option.id !== currentLabelId) ||
      this.entries.some((entry) => !!entry && entry.labelId !== currentLabelId)
    );
  }

  /**
   * Flattens entries into table rows - one per change, one for an entry that has none. See
   * IHistoryRow for why the table is built this way rather than one row per entry.
   */
  private buildRows(entries: List<IAuditHistoryEntry>): IHistoryRow[] {
    const rows: IHistoryRow[] = [];

    (entries ?? List<IAuditHistoryEntry>([])).forEach((entry) => {
      if (!entry) {
        return;
      }

      const changes = this.showsChangeTable(entry) ? (entry.changes ?? []) : [];
      const when = this.parsableDate(entry.occurredAt);

      if (changes.length === 0) {
        rows.push({
          key: entry.id,
          entry,
          startsEntry: true,
          when,
          field: '',
          oldValue: '',
          newValue: '',
          truncated: false,
          actionOnly: true,
        });
        return;
      }

      changes.forEach((change, index) => {
        rows.push({
          key: `${entry.id}:${change?.fieldId ?? index}:${index}`,
          entry,
          startsEntry: index === 0,
          when,
          field: change?.fieldName || change?.fieldId || '',
          oldValue: this.displayValue(change?.oldValue, change?.oldValueTruncated),
          newValue: this.displayValue(change?.newValue, change?.newValueTruncated),
          truncated: this.isTruncated(change),
          actionOnly: false,
        });
      });
    });

    return rows;
  }

  /**
   * The value back again when the date pipe will accept it, null when it will not.
   *
   * The pipe throws an InvalidPipeArgument on an unparseable string, and that throw happens during
   * change detection, so it does not fail one cell - it fails the render of the entire pane.
   */
  private parsableDate(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    return isNaN(new Date(value).getTime()) ? null : value;
  }

  /** Only field-style entries show a before/after table; the rest are described by their action alone. */
  public showsChangeTable(entry: IAuditHistoryEntry): boolean {
    // Membership rows used to be excluded here, because the action read as a sentence - "added this
    // item to Regression Suite" - and a Label row would have repeated the name. Now that the column
    // is [transaction] [field], "Added · Label" has nowhere to put the name, and From/To is where it
    // belongs: the change already carries the removed label on one side and the added one on the
    // other, so a move between labels reads as one row instead of two sentences.
    return !!entry?.changes?.length && entry.action !== AuditHistoryActions.Created;
  }

  public displayValue(value: string | null, truncated: boolean): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    const formatted = this.formatIfTimestamp(value);
    return truncated ? `${formatted}…` : formatted;
  }

  /**
   * Renders a stored timestamp the way the When column renders one, and leaves everything else
   * alone.
   *
   * Date fields - Start Date, Due Date - persist an ISO instant, so the trail was showing
   * "2026-09-29T23:41:00.000Z" where the editor shows a date. Formatting has to happen here rather
   * than on the server: the instant is UTC and only the browser knows the reader's timezone, and
   * 23:41 UTC is the following day in half the world. Doing it server-side would have reported the
   * wrong date to anyone east of Greenwich.
   *
   * The pattern requires the date, the T and a time, so a value that merely starts with digits - a
   * version number, an id, something a person typed - is not reformatted into a date.
   */
  private formatIfTimestamp(value: string): string {
    if (!ISO_TIMESTAMP.test(value)) {
      return value;
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return value;
    }

    try {
      return formatDate(parsed, 'dd MMM yyyy, HH:mm', this.locale);
    } catch {
      // Never worth losing the row over: the raw value still says what changed.
      return value;
    }
  }

  /**
   * The verb alone, for the tag at the front of the What column - "Updated", "Added", "Deleted".
   *
   * Split from the thing it acted on so the column reads as [transaction] [field] rather than as a
   * sentence, which is what makes a long list scannable: the verbs line up.
   */
  public transaction(entry: IAuditHistoryEntry): string {
    if (!entry) {
      return '';
    }

    switch (entry.action) {
      case AuditHistoryActions.Created:
        return 'Created';
      case AuditHistoryActions.Renamed:
        return 'Renamed';
      case AuditHistoryActions.Archived:
        return 'Archived';
      case AuditHistoryActions.Unarchived:
        return 'Restored';
      case AuditHistoryActions.Deleted:
      case AuditHistoryActions.StepDeleted:
        return 'Deleted';
      case AuditHistoryActions.AddedToLabel:
      case AuditHistoryActions.AttachmentAdded:
      case AuditHistoryActions.StepAdded:
      case AuditHistoryActions.StepAttachmentAdded:
        return 'Added';
      case AuditHistoryActions.RemovedFromLabel:
      case AuditHistoryActions.AttachmentRemoved:
      case AuditHistoryActions.StepAttachmentRemoved:
        return 'Removed';
      // Named in full rather than folded into the generic "Added"/"Removed" above: "Added Story"
      // reads as if a label called "Story" had been applied, and the parent-child link is the one
      // fact this row exists to record. Also what the event-type filter's options are built from -
      // see AUDIT_HISTORY_ACTION_LABELS.
      case AuditHistoryActions.ChildAdded:
        return 'Added Child';
      case AuditHistoryActions.ChildRemoved:
        return 'Removed Child';
      case AuditHistoryActions.Moved:
        return 'Moved';
      case AuditHistoryActions.StepReordered:
        return 'Reordered';
      case AuditHistoryActions.Updated:
      case AuditHistoryActions.StepUpdated:
      case AuditHistoryActions.RunUpdated:
        return 'Updated';
      default: {
        // Typed `never`, so a new action without a verb here is a compile error rather than a blank
        // tag. Still returned at runtime - the union describes what the backend sends today, and an
        // unrecognised value is better shown than swallowed.
        const unhandled: never = entry.action;
        return unhandled;
      }
    }
  }

  /**
   * What the transaction acted on: the field's name where there is one, and otherwise the thing the
   * action itself names. Step and run events are qualified, since "Updated Status" on a test would
   * otherwise not say which step or which label it happened on.
   */
  public subjectFor(row: IHistoryRow): string {
    if (!row?.entry) {
      return '';
    }

    const qualifier = this.subjectQualifier(row.entry);
    const field = row.field?.trim();

    // The transaction badge already says "Added Child"/"Removed Child" (see transaction()). When
    // the item type could not be resolved, the backend falls back to that same generic word as the
    // field, and repeating it here would read "[Added Child]" followed by another "Child".
    if (this.isChildAction(row.entry) && field === this.subjectWithoutField(row.entry)) {
      return '';
    }

    if (field) {
      return qualifier ? `${qualifier} · ${field}` : field;
    }

    return qualifier || this.subjectWithoutField(row.entry);
  }

  /** "Step 3", or the run's label - the context a bare field name would be missing. */
  private subjectQualifier(entry: IAuditHistoryEntry): string {
    if (entry.action === AuditHistoryActions.RunUpdated) {
      return entry.runLabelName ? `Run on ${entry.runLabelName}` : 'Run';
    }

    if (STEP_ACTIONS.includes(entry.action) && entry.stepNumber !== null && entry.stepNumber !== undefined) {
      return `Step ${entry.stepNumber}`;
    }

    return '';
  }

  private isChildAction(entry: IAuditHistoryEntry): boolean {
    return entry.action === AuditHistoryActions.ChildAdded || entry.action === AuditHistoryActions.ChildRemoved;
  }

  /** The subject for an entry carrying no field detail at all - a creation, a deletion, a reorder. */
  private subjectWithoutField(entry: IAuditHistoryEntry): string {
    switch (entry.action) {
      case AuditHistoryActions.AttachmentAdded:
      case AuditHistoryActions.AttachmentRemoved:
        return 'Attachment';
      case AuditHistoryActions.AddedToLabel:
      case AuditHistoryActions.RemovedFromLabel:
        return 'Label';
      case AuditHistoryActions.ChildAdded:
      case AuditHistoryActions.ChildRemoved:
        return 'Child';
      case AuditHistoryActions.StepReordered:
        return 'Steps';
      case AuditHistoryActions.Renamed:
        return 'Name';
      default:
        return 'Item';
    }
  }

  public isTruncated(change: IAuditHistoryChange): boolean {
    return !!change && (change.oldValueTruncated || change.newValueTruncated);
  }

  public actorName(entry: IAuditHistoryEntry): string {
    // A change can come from an integration with no Amoeba user behind it.
    return entry?.actorDisplayName ?? entry?.actorUserName ?? 'Unknown user';
  }

  /**
   * Which label the change was made under - the same item is often shared across several labels
   * (e.g. shown on more than one board), and this is what tells apart a Priority change made from
   * one board from one made from another. A dash, not blank, for the rare row that carries no label
   * at all (an attachment linked from a context with no board open) or whose label could not be
   * resolved - consistent with how every other empty value in this table renders (see displayValue).
   */
  public labelName(entry: IAuditHistoryEntry): string {
    return entry?.labelName?.trim() || '—';
  }

  /**
   * The actor's email, for the tooltip on their name - two people can share a display name, and the
   * email is what tells them apart.
   *
   * Snapshotted on the entry like every other name here, so it stays the address the change was
   * actually made from even after the person is renamed or their account is deleted. Null when the
   * change came from an integration or a service account with no user behind it, and also when the
   * username *is* the displayed name - a tooltip repeating the text underneath it is just noise.
   */
  public actorEmail(entry: IAuditHistoryEntry): string | null {
    const email = entry?.actorUserName?.trim();
    if (!email || email.toLowerCase() === this.actorName(entry).trim().toLowerCase()) {
      return null;
    }
    return email;
  }

  /**
   * Shown only for changes that did not originate in the product itself.
   *
   * Matched case-insensitively: the id is whatever was stored when the change was recorded, and a
   * row saved as `Chimera` has the same provenance as one saved as `chimera`. Compared exactly, the
   * drift would show the user a raw internal token instead of "Jira".
   */
  public externalSource(entry: IAuditHistoryEntry): string | null {
    const app = entry?.sourceApp?.trim();
    if (!app) {
      return null;
    }

    const id = app.toLowerCase();
    if (AUDIT_HISTORY_INTERNAL_SOURCE_APPS.includes(id)) {
      return null;
    }

    return AUDIT_HISTORY_SOURCE_APP_LABELS[id] ?? app;
  }

  private get nodeId(): string | null {
    return this.selectedNode?.id ?? null;
  }

  private nodeIdOf(node: INodeInstanceViewModel | null | undefined): string | null {
    return node?.id ?? null;
  }

  private reload(): void {
    const nodeId = this.nodeId;
    const projectId = this.selectedProject?.id;

    if (!nodeId || !projectId) {
      this.historyStore.clear();
      return;
    }

    this.historyStore.loadFirstPage(nodeId, projectId, this.buildQuery());
  }

  /**
   * Reload plus a refresh of the dropdown options. Used where the *item* changed, never where a
   * filter did: the options describe the item's whole history, so refetching them per filter change
   * would cost a request per keystroke and, worse, narrow the list to whatever the current filter
   * left - leaving no way to widen it again.
   */
  private reloadForItem(): void {
    this.reload();

    const nodeId = this.nodeId;
    const projectId = this.selectedProject?.id;
    if (nodeId && projectId) {
      this.historyStore.loadFilterOptions(nodeId, projectId);
    }
  }

  /**
   * Falls back to the id when a name is missing, so a dropdown option is always selectable on
   * screen. Done here rather than trusted on the wire: the label endpoint's own fallback covers a
   * deleted label but not a deleted actor, which can still arrive with `label: null`.
   */
  private withSelectableLabel(options?: IAuditHistoryFilterOption[] | null): IAuditHistoryFilterOption[] {
    return (options ?? []).map((option) => (option?.label ? option : { ...option, label: option?.id ?? '' }));
  }

  /** Null when nothing is filtered, so the store and the API both stay on their no-query path. */
  private buildQuery(): IAuditHistoryQuery | null {
    const query: IAuditHistoryQuery = {};

    if (this.activeScope) {
      query.scope = this.activeScope;
    }
    if (this.selectedActorIds.length) {
      query.actorUserIds = this.selectedActorIds;
    }
    if (this.selectedLabelIds.length) {
      query.labelIds = this.selectedLabelIds;
    }
    if (this.selectedActionTypes.length) {
      query.actions = this.selectedActionTypes;
    }
    if (this.searchText?.trim()) {
      query.searchText = this.searchText.trim();
    }
    // The picker yields a local Date; the stored instants are UTC, so send the instant rather than
    // the wall-clock reading. "To" covers the whole day the user picked - a range of 1st to 1st
    // that excluded everything after midnight would read as a bug.
    const start = this.startOfDay(this.fromDate);
    if (start) {
      query.fromDate = start.toISOString();
    }
    const end = this.endOfDay(this.toDate);
    if (end) {
      query.toDate = end.toISOString();
    }

    return Object.keys(query).length > 0 ? query : null;
  }

  /** Guards its own input even though buildQuery already checks it - every method here does. */
  private startOfDay(date: Date | null): Date | null {
    if (!date) {
      return null;
    }
    const copy = new Date(date.getTime());
    copy.setHours(0, 0, 0, 0);
    return copy;
  }

  private endOfDay(date: Date | null): Date | null {
    if (!date) {
      return null;
    }
    const copy = new Date(date.getTime());
    copy.setHours(23, 59, 59, 999);
    return copy;
  }
}
